import { Radio } from 'antd';
import HmsTableLayout from 'components/HmsTableLayout';
import HmsTableWithFilters from 'components/HmsTableWithFilters';
import useFetchEntities from 'hooks/useFetchEntities';
import useIsMobile from 'hooks/useIsMobile';
import usePageTitle from 'hooks/usePageTitle';
import useSearchParamsMulti from 'hooks/useSearchParamsMulti';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { autoAddIdColumnForExport, autoAddLinkColumnForExport, autoAddLogoColumnForExport, autoAttributesFromColumns, autoExtraAttributesFromColumns, autoRelationsFromColumns } from 'utils/autoColumns';
import { entityColumnsToAntColumns } from 'utils/entityColumnsToAntColumns';
import extractTextFromJSX from 'utils/extractTextFromJsx';
import { unique } from 'utils/oneLiners';

// This function is necessary to avoid initial load of data without filters (which can lead to memory exhaustion on the backend)
// Fix proposed on 2026-02-19 by Cursor Composer 1.5
function getEffectiveFiltersFromFilterGroups(filterGroups, filterGroupSelections) {
  if (!filterGroups?.length) return {};
  let effectiveFilters = {};
  const selections = (filterGroupSelections ?? '').split(',');
  for (let i = 0; i < filterGroups.length; i++) {
    const group = filterGroups[i];
    const selectedLabel = selections[i];
    const selected = group?.find(g => g.label == selectedLabel);
    if (selected?.filters) {
      effectiveFilters = { ...effectiveFilters, ...selected.filters };
    }
  }
  return effectiveFilters;
}

const HmsEntityTableController = forwardRef(({ entity, relations: relationsParam, onlyAttributes, extraAttributes: extraAttributesParam, 
  filterGroups, showColumns, columnSets: columnSetsParam, onRowClickLink, canCreate = true, createUrl = null, exportEnabled = true, columnSelectorEnabled = true, filterEnabled = true, searchEnabled = true,
  renderMobileRow, mobileRowRequiredAttributes, extraButtonBarItems, extraButtonsLeft, selectionEnabled = false, onSelectionChange, rowKey, showSelectAllButtons = true, skipAutoLinkColumn = false, leanRelations = false }, ref) => {
  const { useSearchParamSingle } = useSearchParamsMulti();
  const tableRef = useRef();

  useImperativeHandle(ref, () => ({
    reload: () => {
      reload();
    },
    // Expose HmsTable ref methods
    getSelectedRows: () => tableRef.current?.getSelectedRows?.(),
    getSelectedRowKeys: () => tableRef.current?.getSelectedRowKeys?.(),
    clearSelection: () => tableRef.current?.clearSelection?.(),
    clearAllSelections: () => tableRef.current?.clearAllSelections?.(),
    selectAll: () => tableRef.current?.selectAll?.(),
    unselectAll: () => tableRef.current?.unselectAll?.(),
  }));

  const columnSets = useMemo(() => {
    return columnSetsParam?.map(cs => ({
      ...cs,
      key: cs.key ?? extractTextFromJSX(cs.name)?.replace(' ', ''),
    }));
  }, [columnSetsParam]);

  const [controllerColumnSet, setControllerColumnSet] = useSearchParamSingle('controllerColumnSet', columnSets?.[0]?.key);
  const currentColumnSet = columnSets?.find(cs => cs.key == controllerColumnSet);

  const defaultFilterGroupSelections = (filterGroups ?? []).map(group => (group.find(i => i.isDefault) ?? group[0])?.label).join(',');
  const [filterGroupSelections] = useSearchParamSingle('filterGroupSelections', defaultFilterGroupSelections, { type: 'string' });

  const [filters, setFiltersFromColumnSets] = useState(() => {
    const columnSetFilters = currentColumnSet?.filters ?? {};
    const filterGroupFilters = getEffectiveFiltersFromFilterGroups(filterGroups, filterGroupSelections);
    return { ...columnSetFilters, ...filterGroupFilters };
  });

  const isMobile = useIsMobile();
  const isMobileVersion = isMobile && renderMobileRow;

  const columns =
    currentColumnSet?.columns
    ?? showColumns;

  const tableColumns = entityColumnsToAntColumns(entity, columns);

  autoAddIdColumnForExport(entity, tableColumns);
  if (!skipAutoLinkColumn) {
    autoAddLinkColumnForExport(entity, tableColumns);
  }
  autoAddLogoColumnForExport(entity, tableColumns);

  const relations =
    currentColumnSet?.relations
    ?? relationsParam
    ?? autoRelationsFromColumns(entity, tableColumns);

  const extraAttributes =
    currentColumnSet?.extraAttributes
    ?? extraAttributesParam
    ?? autoExtraAttributesFromColumns(entity, tableColumns);

  const onlyAttributesFinal = useMemo(() => {
    const baseAttributes = onlyAttributes
      ?? currentColumnSet?.onlyAttributes
      ?? autoAttributesFromColumns(entity, columns);
    
    // Normalize baseAttributes to array
    const baseAttrsArray = Array.isArray(baseAttributes)
      ? baseAttributes
      : (typeof baseAttributes === 'string' ? baseAttributes.split(',').map(a => a.trim()).filter(a => a) : []);
    
    // Add mobile row required attributes if renderMobileRow is provided
    if (renderMobileRow && mobileRowRequiredAttributes) {
      const mobileAttrs = Array.isArray(mobileRowRequiredAttributes) 
        ? mobileRowRequiredAttributes 
        : mobileRowRequiredAttributes.split(',').map(a => a.trim()).filter(a => a);
      return unique([...baseAttrsArray, ...mobileAttrs]).join(',');
    }
    
    return Array.isArray(baseAttributes) ? baseAttributes.join(',') : baseAttributes;
  }, [onlyAttributes, currentColumnSet?.onlyAttributes, entity, columns, renderMobileRow, mobileRowRequiredAttributes]);

  const { data, isError, reload } = useFetchEntities(entity.name, { filters, relations, extraAttributes, onlyAttributes: onlyAttributesFinal, leanRelations });
  usePageTitle(entity.displayNamePlural);

  const onFiltersChanged = (newFilters) => {
    setFiltersFromColumnSets(newFilters);
  }

  const setColumnSet = (key) => {
    setControllerColumnSet(key);
    const newColumnSet = columnSets.find(cs => cs.key == key);
    const columnSetFilters = newColumnSet?.filters ?? {};
    const filterGroupFilters = getEffectiveFiltersFromFilterGroups(filterGroups, filterGroupSelections);
    setFiltersFromColumnSets({ ...columnSetFilters, ...filterGroupFilters });
  }

  // TODO: remove shitFilter !!!
  const shitFilter = columnSets?.find(cs => cs.key == controllerColumnSet)?.shitFilter;

  const ColumnSetSelector = () => (
    <Radio.Group
      size="small"
      value={controllerColumnSet}
      onChange={(e) => setColumnSet(e.target.value)}
      buttonStyle="solid"
    >
      {columnSets.map((cs) => (
        <Radio.Button
          key={cs.name}
          value={cs.key ?? cs.name}
        >
          {cs.name}
        </Radio.Button>
      ))}
    </Radio.Group>
  )

  // TODO: replace with useMemo
  const filteredData = shitFilter ? data?.filter(shitFilter) : data;

  return (
    <>
      <HmsTableLayout breadcrumbs={entity.entitiesBreadcrumb}>
        <HmsTableWithFilters
          ref={tableRef}
          persistentId={entity.name}
          exportEnabled={exportEnabled}
          filterEnabled={!isMobileVersion && filterEnabled}
          columnSelectorEnabled={columnSelectorEnabled}
          columns={tableColumns}
          extraButtonsLeft={extraButtonsLeft}
          extraButtonBarItems={
            ({ filteredData }) => {
              return (
                (columnSets || extraButtonBarItems) &&
                <>
                  {columnSets && <ColumnSetSelector />}
                  {typeof extraButtonBarItems == 'function' ? extraButtonBarItems({ filteredData }) : extraButtonBarItems}
                </>
              )
            }
          }
          data={filteredData}
          isError={isError}
          title={entity.displayNamePlural}
          exportPrefix={entity.displayNamePlural + (currentColumnSet ? '-' + currentColumnSet?.key : '')}
          createButtonLink={canCreate && (createUrl ?? entity.newEntityUrl())}
          createButtonType="primary"
          filterGroups={filterGroups}
          onFiltersChanged={onFiltersChanged}
          onRowClickedLink={onRowClickLink ?? ((record) => entity.entityUrl(record))}
          renderMobileRow={renderMobileRow}
          fixedX={true}
          fixedY={true}
          searchEnabled={searchEnabled}
          selectionEnabled={selectionEnabled}
          onSelectionChange={onSelectionChange}
          rowKey={rowKey}
          showSelectAllButtons={showSelectAllButtons}
        />
      </HmsTableLayout>

    </>
  )
});

HmsEntityTableController.displayName = 'HmsEntityTableController';

export default HmsEntityTableController